import React, { useState, useCallback, useEffect } from 'react';
import './CommentsTab.css';

const CommentsTab = ({ asset, onUpdate, formData, onFormDataChange }) => {
  const [comments, setComments] = useState(formData?.comments?.generalComments || '');
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(formData?.comments?.lastUpdated || null);
  
  const currentUser = {
    name: 'Current User',
    email: 'user@company.com',
    role: 'Asset Manager'
  }; // TODO: Replace with actual user data when authentication is available
  
  // Debounce utility function
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Auto-save functionality with debounce
  const debouncedSave = useCallback(
    debounce(async (commentText) => {
      if (commentText !== (formData?.comments?.generalComments || '')) {
        setIsSaving(true);
        try {
          await saveAssetComments(commentText);
          setLastSaved(new Date());
          setIsModified(false);
        } catch (error) {
          console.error('Failed to save comments:', error);
        } finally {
          setIsSaving(false);
        }
      }
    }, 1000),
    [formData?.comments?.generalComments]
  );

  const saveAssetComments = async (commentText) => {
    // Create comment history entry
    const historyEntry = {
      timestamp: new Date().toISOString(),
      updatedBy: currentUser.name,
      previousContent: formData?.comments?.generalComments || '',
      newContent: commentText,
      changeType: commentText === '' ? 'cleared' : 
                  (formData?.comments?.generalComments || '') === '' ? 'created' : 'updated'
    };

    // Update form data with new comments
    const updatedComments = {
      generalComments: commentText,
      lastUpdated: new Date().toISOString(),
      updatedBy: currentUser.name,
      commentHistory: [historyEntry, ...(formData?.comments?.commentHistory || [])]
    };

    // Update the parent form data
    onFormDataChange({
      ...formData,
      comments: updatedComments
    });

    // If onUpdate callback exists, call it
    if (onUpdate) {
      onUpdate();
    }
  };
  
  const handleCommentsChange = (e) => {
    const newComments = e.target.value;
    setComments(newComments);
    setIsModified(true);
    debouncedSave(newComments);
  };
  
  const handleManualSave = async () => {
    if (isModified) {
      setIsSaving(true);
      try {
        await saveAssetComments(comments);
        setLastSaved(new Date());
        setIsModified(false);
      } catch (error) {
        console.error('Failed to save comments:', error);
      } finally {
        setIsSaving(false);
      }
    }
  };
  
  const handleClearComments = () => {
    if (window.confirm('Are you sure you want to clear all comments? This action cannot be undone.')) {
      setComments('');
      setIsModified(true);
      debouncedSave('');
    }
  };

  const formatRelativeTime = (date) => {
    if (!date) return '';
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - new Date(date)) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return new Date(date).toLocaleDateString();
  };

  // Update comments when formData changes
  useEffect(() => {
    if (formData?.comments?.generalComments !== comments) {
      setComments(formData?.comments?.generalComments || '');
      setIsModified(false);
    }
  }, [formData?.comments?.generalComments]);
  
  return (
    <div className="comments-tab">
      <div className="comments-header">
        <h3>Asset Comments</h3>
        <p className="tab-description">
          Add general comments, observations, or notes about this asset.
          Comments are automatically saved as you type.
        </p>
      </div>
      
      <div className="comments-content">
        <div className="comment-input-section">
          <label htmlFor="assetComments" className="form-label">
            General Comments
          </label>
          
          <textarea
            id="assetComments"
            className="form-control comments-textarea"
            placeholder="Add your comments about this asset here...

Examples:
• Important configuration details
• Known issues or limitations  
• Contact information for support
• Historical context or changes
• Special requirements or considerations"
            value={comments}
            onChange={handleCommentsChange}
            rows="12"
          />
          
          <div className="comments-footer">
            <div className="comments-status">
              {isSaving && (
                <span className="saving-indicator">
                  <span className="spinner"></span>
                  Saving...
                </span>
              )}
              
              {!isSaving && isModified && (
                <span className="modified-indicator">
                  <span className="modified-dot"></span>
                  Unsaved changes
                </span>
              )}
              
              {!isSaving && !isModified && lastSaved && (
                <span className="saved-indicator">
                  <span className="saved-check">✓</span>
                  Saved {formatRelativeTime(lastSaved)}
                </span>
              )}
            </div>
            
            <div className="comments-actions">
              {comments.length > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleClearComments}
                  disabled={isSaving}
                >
                  Clear Comments
                </button>
              )}
              
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleManualSave}
                disabled={!isModified || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Now'}
              </button>
            </div>
          </div>
        </div>
        
        {formData?.comments?.commentHistory && formData.comments.commentHistory.length > 0 && (
          <div className="comment-history-section">
            <h4>Recent Changes</h4>
            <div className="comment-history-list">
              {formData.comments.commentHistory
                .slice(0, 3)
                .map((entry, index) => (
                  <div key={index} className="history-entry">
                    <div className="history-meta">
                      <span className="history-user">{entry.updatedBy}</span>
                      <span className="history-time">{formatRelativeTime(entry.timestamp)}</span>
                    </div>
                    <div className="history-action">
                      {entry.changeType === 'created' && 'Added comments'}
                      {entry.changeType === 'updated' && 'Updated comments'}
                      {entry.changeType === 'cleared' && 'Cleared comments'}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentsTab; 